import { useEffect } from 'react';

const useRecaptchaChallengeDisplay = (
  recaptchaChallengeElementZIndex: string
) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    const updateReCaptchaChallengeElementPositionDetails = (
      mainChallengeElement: HTMLElement,
      iframeParentElement: HTMLElement
    ) => {
      const { position: iframeParentPosition } =
        window.getComputedStyle(iframeParentElement);

      // Looks like for smaller screen sizes, the parent of the iframe has position fixed and
      // because we have no zooming here, we don't want to do anything else so everything should work like before
      if (iframeParentPosition === 'fixed') return;

      // Need to update the overlay because it has position fixed initially but
      // because we use transform on it's parent, the position needs to be updated
      // so it still takes the entire viewport
      const overlayElement = mainChallengeElement.children[0] as HTMLElement;
      overlayElement.style.setProperty('position', 'absolute');
      overlayElement.style.setProperty('width', '100vw');
      overlayElement.style.setProperty('height', '100vh');
      overlayElement.style.setProperty('top', '-50vh');
      overlayElement.style.setProperty('left', '-50vw');

      // We want to make sure the iframe container overflows when it exceeds the viewport (e.g. when zooming in)
      iframeParentElement.style.setProperty('max-width', '90vw');
      iframeParentElement.style.setProperty('max-height', '90vh');
      iframeParentElement.style.setProperty('overflow', 'auto');
      iframeParentElement.style.setProperty('box-sizing', 'content-box');

      const challengeElementResizeObserver = new ResizeObserver((entries) => {
        if (!Array.isArray(entries)) return;

        const entry = entries[0];
        if (!entry?.contentRect) return;

        const { width: challengeElementWidth, height: challengeElementHeight } =
          entry.contentRect;

        // We need to make sure the overlay updates its position when the challenge element size changes
        overlayElement.style.setProperty(
          'transform',
          `translate(${challengeElementWidth / 2}px, ${challengeElementHeight / 2}px)`
        );
      });

      let prevChallengeVisibility =
        window.getComputedStyle(mainChallengeElement).visibility;

      // When we resize the viewport by zooming for example, the challenge element top and left position
      // are updated as well, so we need to make sure they always stay in the middle of the screen so it is
      // properly displayed. That's why we make this change inside the observer in order to overwrite the default position
      const challengeElementMutationObserver = new MutationObserver(
        (mutationsList) => {
          for (const mutation of mutationsList) {
            if (
              mutation.type === 'attributes' &&
              (mutation.attributeName === 'style' ||
                mutation.attributeName === 'class')
            ) {
              mainChallengeElement.style.setProperty(
                'transform',
                'translate(-50%, -50%)'
              );
              mainChallengeElement.style.setProperty('top', '50%');
              mainChallengeElement.style.setProperty('left', '50%');

              const newChallengeVisibility =
                window.getComputedStyle(mainChallengeElement).visibility;
              if (newChallengeVisibility !== prevChallengeVisibility) {
                prevChallengeVisibility = newChallengeVisibility;

                if (newChallengeVisibility === 'visible') {
                  challengeElementResizeObserver.observe(mainChallengeElement);
                } else {
                  challengeElementResizeObserver.disconnect();
                }
              }
            }
          }
        }
      );

      challengeElementMutationObserver.observe(mainChallengeElement, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    };

    const updateReCaptchaChallengeElementPosition = (
      reCaptchaChallengeElement: HTMLDivElement
    ) => {
      const iframeElement =
        reCaptchaChallengeElement?.getElementsByTagName?.('iframe')?.[0];
      const mainChallengeElement =
        iframeElement?.parentElement?.style.position === 'absolute'
          ? iframeElement.parentElement
          : iframeElement?.parentElement?.parentElement;
      mainChallengeElement?.style.setProperty('position', 'fixed');

      if (!mainChallengeElement?.style.zIndex) {
        mainChallengeElement?.style.setProperty(
          'z-index',
          recaptchaChallengeElementZIndex
        );
      }

      if (mainChallengeElement && iframeElement?.parentElement) {
        updateReCaptchaChallengeElementPositionDetails(
          mainChallengeElement,
          iframeElement.parentElement
        );
      }
    };

    const updateReCaptchaArrowsDisplay = (
      reCaptchaChallengeElement: HTMLDivElement
    ) => {
      const arrows = Array.from(
        reCaptchaChallengeElement?.getElementsByClassName?.(
          'g-recaptcha-bubble-arrow'
        ) ?? []
      ) as HTMLElement[];

      arrows.forEach((arrow) => {
        arrow.style.setProperty('display', 'none');
      });
    };

    // We need to intercept when ReCaptcha injects the challenge element in the DOM.
    // In order to avoid false positives and because the injected element does not have
    // any unique identifiers (ids, classes etc), we check that the injected element is a DIV
    // and contains an iframe with a given recaptcha related value in its src attribute.
    const bodyMutationObserver = new MutationObserver((mutationsList) => {
      const addedNode = mutationsList[0]?.addedNodes[0];
      if (addedNode?.nodeName === 'DIV') {
        const addedDivElement = addedNode as HTMLDivElement;
        const recaptchaIFrame = addedDivElement?.querySelector(
          'iframe[src*="recaptcha/api2/bframe"]'
        );

        if (recaptchaIFrame) {
          updateReCaptchaChallengeElementPosition(addedDivElement);
          updateReCaptchaArrowsDisplay(addedDivElement);

          // We don't disconnect the observer here because recaptcha is reset
          // after 2 minutes of inactivity and the element is removed and added again in the DOM,
          // so we need to make sure the challenge is displayed ok multiple times
        }
      }
    });

    bodyMutationObserver.observe(document.body, {
      subtree: false,
      childList: true,
    });
  }, []);
};

export default useRecaptchaChallengeDisplay;
